import React from "react";
import styled, { css } from "styled-components";
import { breakpoints, colors } from "../../../../theme";
import { POINTER_HOVER_MEDIA } from "../../../styles/pointer-hover";
import { logoImage } from "../../../utils/cdn";

/** Circular badge size - similar to GitHub Achievements icons. */
const BADGE_SIZE = 64;

export const NETWORK_BADGES = [
  {
    id: "toptal",
    src: logoImage("dark/toptal-mark"),
    alt: "Toptal",
    href: "https://www.toptal.com/developers/resume/parmeet-singh",
    width: 59,
    height: 91,
  },
  {
    id: "andela",
    src: logoImage("dark/andela-mark"),
    alt: "Andela",
    href: "https://www.andela.com/",
    width: 102,
    height: 111,
  },
  {
    id: "turing",
    src: logoImage("dark/turing-mark"),
    alt: "Turing",
    href: "https://matching.turing.com/developer-resume-preview/e905969c1e9d992993e884d1c1d42814905e1879b608e6",
    width: 86,
    height: 50,
  },
];

const visibilityStyles = {
  /** Sidebar: desktop only (tablet+). */
  sidebar: css`
    display: none;

    @media only screen and (min-width: ${breakpoints.tablet}) {
      display: block;
      margin: 1.5rem auto 0;
      max-width: 640px;
    }
  `,
  /** Intro (after Bio): mobile only. */
  mobile: css`
    display: block;
    margin-top: 1.5rem;

    @media only screen and (min-width: ${breakpoints.tablet}) {
      display: none;
    }
  `,
};

const Section = styled.section`
  ${(props) => visibilityStyles[props.$placement]}
`;

const Title = styled.h2`
  margin: 0 0 0.75rem;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.35;
  color: #111827;
`;

const BadgeList = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
`;

const BadgeLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: ${BADGE_SIZE}px;
  height: ${BADGE_SIZE}px;
  flex-shrink: 0;
  padding: 0.625rem;
  border-radius: 50%;
  border: 1px solid #d1d5db;
  background: #fff;
  text-decoration: none;
  box-shadow: 0 1px 2px color-mix(in srgb, #111827 6%, transparent);
  transition: border-color 0.2s ease, box-shadow 0.2s ease,
    background-color 0.2s ease;

  @media ${POINTER_HOVER_MEDIA} {
    &:hover {
      border-color: ${colors.linkHover.light};
      background-color: ${colors.accentLight};
      box-shadow: 0 2px 6px color-mix(in srgb, #111827 10%, transparent);
    }
  }

  &:focus-visible {
    outline: 2px solid
      color-mix(in srgb, ${colors.linkHover.light} 50%, transparent);
    outline-offset: 2px;
  }

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

/**
 * @param {"sidebar" | "mobile"} placement
 *   sidebar - shown from tablet up, under Download resume
 *   mobile  - shown below Bio on small screens only
 */
export default function VettedBy({ placement = "sidebar" }) {
  const titleId = `vetted-by-title-${placement}`;

  return (
    <Section $placement={placement} aria-labelledby={titleId}>
      <Title id={titleId}>Vetted by</Title>
      <BadgeList>
        {NETWORK_BADGES.map(({ id, src, alt, href, width, height }) => (
          <BadgeLink
            key={id}
            href={href}
            target="_blank"
            rel="noopener noreferrer nofollow"
            aria-label={`Visit ${alt}`}
            title={alt}
          >
            <img
              src={src}
              alt={alt}
              width={width}
              height={height}
              loading="lazy"
              decoding="async"
            />
          </BadgeLink>
        ))}
      </BadgeList>
    </Section>
  );
}
